// src/components/admin/SystemStatus.tsx
import React from 'react';
import { cn } from '@/core/lib/utils';

// Type for the backend SystemHealthResponse
interface BackendSystemHealth {
  overall_status: 'healthy' | 'degraded' | 'unhealthy' | 'unknown';
  version: string;
  environment: string;
  uptime_seconds: number;
  database: {
    status: string;
    connection_pool_size: number;
    active_connections: number;
    waiting_connections: number;
    response_time_ms: number;
    version: string;
  };
  cache: {
    status: string;
    connected_clients: number;
    used_memory_mb: number;
    max_memory_mb: number;
    memory_usage_percent: number;
    hit_rate_percent: number;
    response_time_ms: number;
  };
  storage: {
    status: string;
    bucket_count: number;
    total_size_gb: number;
    response_time_ms: number;
  };
  queue: {
    status: string;
    pending_tasks: number;
    active_workers: number;
    tasks_per_minute: number;
    failed_tasks_24h: number;
    success_rate_percent: number;
  };
  services: Array<{
    name: string;
    status: string;
    response_time_ms?: number;
    last_check: string;
    message?: string;
    details?: Record<string, unknown>;
  }>;
  cpu_usage_percent: number;
  memory_usage_percent: number;
  disk_usage_percent: number;
  checked_at: string;
}

// Type for BackgroundJob from backend
interface BackendJob {
  id: string;
  name: string;
  task_name: string;
  status: 'pending' | 'started' | 'success' | 'failure' | 'retry' | 'revoked';
  priority: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  runtime_seconds?: number;
  queue_name: string;
  args?: unknown[];
  kwargs?: Record<string, unknown>;
  error?: string;
}

interface SystemStatusProps {
  health: BackendSystemHealth;
  jobs?: BackendJob[];
  onRetryJob?: (jobId: string) => void;
  onClearCache?: () => void;
  className?: string;
}

export const SystemStatus: React.FC<SystemStatusProps> = ({
  health,
  jobs = [],
  onRetryJob,
  onClearCache,
  className,
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-500';
      case 'degraded':
        return 'bg-yellow-500';
      case 'unhealthy':
      case 'down':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-100 text-green-800';
      case 'degraded':
        return 'bg-yellow-100 text-yellow-800';
      case 'unhealthy':
      case 'down':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getJobStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'failure':
        return 'bg-red-100 text-red-800';
      case 'started':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'retry':
        return 'bg-orange-100 text-orange-800';
      case 'revoked':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatUptime = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return '0d 0h 0m';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  // Safely get numeric values with fallback
  const safeNumber = (value: number | undefined, fallback: number = 0) => {
    return typeof value === 'number' && !isNaN(value) ? value : fallback;
  };

  const cpuUsage = safeNumber(health?.cpu_usage_percent);
  const memoryUsage = safeNumber(health?.memory_usage_percent);
  const diskUsage = safeNumber(health?.disk_usage_percent);
  const uptimeSeconds = safeNumber(health?.uptime_seconds);
  const activeConnections = safeNumber(health?.database?.active_connections);
  const requestsPerMinute = safeNumber(health?.queue?.tasks_per_minute);
  const errorRate = 100 - safeNumber(health?.queue?.success_rate_percent, 100);

  return (
    <div className={cn('space-y-6', className)}>
      {/* Overall Status */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-navy">System Health</h3>
          <span className={cn(
            'px-3 py-1 rounded-full text-sm font-medium capitalize',
            getStatusBadge(health?.overall_status || 'unknown')
          )}>
            {health?.overall_status || 'unknown'}
          </span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-cream rounded-lg">
            <p className="text-2xl font-bold text-navy">{formatUptime(uptimeSeconds)}</p>
            <p className="text-sm text-warm-gray">Uptime</p>
          </div>
          <div className="text-center p-4 bg-cream rounded-lg">
            <p className="text-2xl font-bold text-navy">{activeConnections}</p>
            <p className="text-sm text-warm-gray">Active Connections</p>
          </div>
          <div className="text-center p-4 bg-cream rounded-lg">
            <p className="text-2xl font-bold text-navy">{requestsPerMinute.toFixed(1)}</p>
            <p className="text-sm text-warm-gray">Tasks/min</p>
          </div>
          <div className="text-center p-4 bg-cream rounded-lg">
            <p className="text-2xl font-bold text-navy">{errorRate.toFixed(2)}%</p>
            <p className="text-sm text-warm-gray">Error Rate</p>
          </div>
        </div>
      </div>

      {/* Services Status */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-navy mb-4">Services</h3>
        <div className="space-y-3">
          {(health?.services || []).map((service) => (
            <div key={service.name} className="flex items-center justify-between p-3 bg-cream rounded-lg">
              <div className="flex items-center gap-3">
                <div className={cn('w-3 h-3 rounded-full', getStatusColor(service.status))} />
                <span className="font-medium text-navy capitalize">{service.name}</span>
              </div>
              <div className="flex items-center gap-4">
                {service.response_time_ms !== undefined && (
                  <span className="text-sm text-warm-gray">{service.response_time_ms.toFixed(0)}ms</span>
                )}
                <span className={cn(
                  'px-2 py-0.5 rounded text-xs font-medium capitalize',
                  getStatusBadge(service.status)
                )}>
                  {service.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Resource Usage */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-navy">Resource Usage</h3>
          {onClearCache && (
            <button
              onClick={onClearCache}
              className="text-sm text-sage hover:text-sage-dark"
            >
              Clear Cache
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* CPU */}
          <div className="p-4 bg-cream rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-navy">CPU</span>
              <span className="text-sm text-warm-gray">{cpuUsage.toFixed(1)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={cn(
                  'h-2 rounded-full transition-all',
                  cpuUsage > 80 ? 'bg-red-500' : cpuUsage > 60 ? 'bg-yellow-500' : 'bg-sage'
                )}
                style={{ width: `${Math.min(cpuUsage, 100)}%` }}
              />
            </div>
          </div>

          {/* Memory */}
          <div className="p-4 bg-cream rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-navy">Memory</span>
              <span className="text-sm text-warm-gray">{memoryUsage.toFixed(1)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={cn(
                  'h-2 rounded-full transition-all',
                  memoryUsage > 80 ? 'bg-red-500' : memoryUsage > 60 ? 'bg-yellow-500' : 'bg-sage'
                )}
                style={{ width: `${Math.min(memoryUsage, 100)}%` }}
              />
            </div>
          </div>

          {/* Disk */}
          <div className="p-4 bg-cream rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-navy">Disk</span>
              <span className="text-sm text-warm-gray">{diskUsage.toFixed(1)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={cn(
                  'h-2 rounded-full transition-all',
                  diskUsage > 80 ? 'bg-red-500' : diskUsage > 60 ? 'bg-yellow-500' : 'bg-sage'
                )}
                style={{ width: `${Math.min(diskUsage, 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Background Jobs */}
      {jobs.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-navy mb-4">Background Jobs</h3>
          <div className="space-y-2">
            {jobs.map((job) => (
              <div key={job.id} className="flex items-center justify-between p-3 bg-cream rounded-lg">
                <div className="flex-1">
                  <p className="font-medium text-navy">{job.name}</p>
                  <p className="text-sm text-warm-gray">{job.queue_name}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={cn(
                    'px-2 py-0.5 rounded text-xs font-medium capitalize',
                    getJobStatusBadge(job.status)
                  )}>
                    {job.status}
                  </span>
                  {job.status === 'failure' && onRetryJob && (
                    <button
                      onClick={() => onRetryJob(job.id)}
                      className="text-sm text-sage hover:text-sage-dark"
                    >
                      Retry
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemStatus;
